import { FormEvent, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  FloatingPortal,
  FloatingOverlay,
  FloatingFocusManager,
  useFloating,
  useDismiss,
  useInteractions,
} from "@floating-ui/react";
import { Tooltip } from "@archlang/ui";
import {
  useOutOfHostSettingsPages,
  useOutOfHostCtx,
  PluginBoundary,
  type SettingsScope,
} from "@archlang/app-shell/board";
import { wsHome } from "../lib/paths";
import { slugErrorKind } from "../workspace/slugError";
import { useMe } from "../api/hooks/useMe";
import { useWorkspace } from "../api/hooks/useWorkspace";
import { useRepos } from "../repos/useRepos";
import { McpSetupPanel } from "../mcp/McpSetupPanel";
import { useMembers } from "../api/hooks/useMembers";
import { useInvites } from "../api/hooks/useInvites";
import { useUpdateProfile } from "../account/useUpdateProfile";
import { usePasswordChange } from "../account/usePasswordChange";
import { useUpdateWorkspace } from "../workspace/useUpdateWorkspace";
import { useDeleteWorkspace } from "../workspace/useDeleteWorkspace";
import { useUpdateMemberRole } from "../workspace/useUpdateMemberRole";
import { useRemoveMember } from "../workspace/useRemoveMember";
import { useRevokeInvite } from "../workspace/useRevokeInvite";
import { InviteModal } from "../workspace/InviteModal";
import { useTokensList, useCreateToken, useRevokeToken } from "../tokens/useTokens";
import { NewTokenModal } from "../tokens/NewTokenModal";
import { RevokeConfirmDialog } from "../tokens/RevokeConfirmDialog";
import { TokensTable } from "../tokens/TokensTable";
import { Field } from "../ui/Field";
import { TextInput } from "../ui/TextInput";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { Spinner } from "../ui/Spinner";
import { Avatar } from "../ui/Avatar";
import { useToast } from "../ui/Toast";
import { Table, THead, TBody, Tr, Th, Td } from "../ui/Table";
import { SkeletonRows } from "../ui/Skeleton";
import { useTranslation, apiErrorMessage, useLocale, setLocale, LOCALES } from "../i18n";
import type { TokenRow } from "../tokens/types";

// ── helpers ──────────────────────────────────────────────────────────────────

function previewBranch(pattern: string): string {
  return pattern
    .replace(/\{author\}/g, "jane")
    .replace(/\{slug\}/g, "add-billing")
    .replace(/\{id\}/g, "01ktab")
    .replace(/\{date\}/g, "2026-06-03");
}

// ── SVG icons ─────────────────────────────────────────────────────────────────

function IconProfile() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8" />
    </svg>
  );
}

function IconPassword() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function IconGeneral() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0" />
    </svg>
  );
}

function IconMembers() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 100 8 4 4 0 000-8M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function IconTokens() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
    </svg>
  );
}

function IconGit() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0" />
    </svg>
  );
}

function IconMcp() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="8" width="16" height="12" rx="2" />
      <path d="M12 8V4M9 4h6M9 14h.01M15 14h.01M9 17h6" />
    </svg>
  );
}

function IconDanger() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function IconClose() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

// ── tab definitions ───────────────────────────────────────────────────────────

interface TabDef {
  key: string;
  /** i18n key for a built-in tab; ignored when `rawLabel` is set. */
  labelKey: string;
  /** A pre-resolved label (contributed settings pages carry raw titles). */
  rawLabel?: string;
  icon: React.ReactNode;
}

const ACCOUNT_TABS: TabDef[] = [
  { key: "profile", labelKey: "settings.tabs.profile", icon: <IconProfile /> },
  { key: "password", labelKey: "settings.tabs.password", icon: <IconPassword /> },
];

const WORKSPACE_TABS: TabDef[] = [
  { key: "ws-general", labelKey: "settings.tabs.wsGeneral", icon: <IconGeneral /> },
  { key: "ws-members", labelKey: "settings.tabs.wsMembers", icon: <IconMembers /> },
  { key: "ws-tokens", labelKey: "settings.tabs.wsTokens", icon: <IconTokens /> },
];

const REPO_TABS: TabDef[] = [
  { key: "repo-git", labelKey: "settings.tabs.repoGit", icon: <IconGit /> },
  { key: "repo-mcp", labelKey: "settings.tabs.repoMcp", icon: <IconMcp /> },
  { key: "repo-danger", labelKey: "settings.tabs.repoDanger", icon: <IconDanger /> },
];

// ── panels ────────────────────────────────────────────────────────────────────

function ProfilePanel() {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const toast = useToast();
  const me = useMe();
  const update = useUpdateProfile();
  const [displayName, setDisplayName] = useState<string | null>(null);
  const { locale } = useLocale();

  const currentName = displayName ?? (me.data?.displayName ?? "");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    update.mutate(
      { displayName: currentName },
      {
        onSuccess: () => toast.success(t("settings.profile.saved")),
      },
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-slate-100">{t("settings.tabs.profile")}</h2>
      {update.isSuccess && (
        <Banner kind="success">{t("settings.profile.saved")}</Banner>
      )}
      {update.error && (
        <Banner kind="error">{apiErrorMessage(update.error, tErr)}</Banner>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label={t("settings.profile.email")}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              value={me.data?.email ?? ""}
              disabled
              {...ariaProps}
            />
          )}
        </Field>
        <Field label={t("settings.profile.displayName")}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              value={currentName}
              onChange={(e) => setDisplayName(e.target.value)}
              {...ariaProps}
            />
          )}
        </Field>
        <div className="flex justify-end">
          <Button variant="primary" type="submit" disabled={update.isPending}>
            {update.isPending ? <Spinner size="sm" /> : t("settings.save")}
          </Button>
        </div>
      </form>

      <div className="border-t border-slate-700/60 pt-6">
        <h3 className="mb-3 text-sm font-semibold text-slate-300">{t("settings.profile.language")}</h3>
        <div className="flex flex-wrap gap-2">
          {LOCALES.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => setLocale(l.id)}
              className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                locale === l.id
                  ? "border-arch-500 bg-arch-500/15 text-arch-200"
                  : "border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-600 hover:text-slate-100"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PasswordPanel() {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const change = usePasswordChange();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mismatch, setMismatch] = useState(false);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    change.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
        },
      },
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-slate-100">{t("settings.tabs.password")}</h2>
      {change.isSuccess && (
        <Banner kind="success">{t("settings.password.changed")}</Banner>
      )}
      {mismatch && (
        <Banner kind="error">{t("settings.password.mismatch")}</Banner>
      )}
      {change.error && (
        <Banner kind="error">{apiErrorMessage(change.error, tErr)}</Banner>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label={t("settings.password.current")}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              {...ariaProps}
            />
          )}
        </Field>
        <Field label={t("settings.password.new")}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              {...ariaProps}
            />
          )}
        </Field>
        <Field label={t("settings.password.confirm")}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              {...ariaProps}
            />
          )}
        </Field>
        <div className="flex justify-end">
          <Button variant="primary" type="submit" disabled={change.isPending}>
            {change.isPending ? <Spinner size="sm" /> : t("settings.save")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function WsGeneralPanel({ slug }: { slug: string }) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const ws = useWorkspace(slug);
  const update = useUpdateWorkspace(slug);
  const deleteWs = useDeleteWorkspace(slug);
  const [name, setName] = useState<string | null>(null);
  const [slugValue, setSlugValue] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  const currentName = name ?? (ws.data?.name ?? "");
  const currentSlug = slugValue ?? (ws.data?.slug ?? "");
  const isOwner = ws.data?.role === "owner";
  // Reserved vs taken, on the field rather than in a banner. Without this the
  // reserved code has no catalog entry at this surface and apiErrorMessage
  // falls back to echoing the server's raw English into a localised UI.
  const wsSlugErrorKind = slugErrorKind(update.error);

  function handleSave(e: FormEvent) {
    e.preventDefault();
    update.mutate(
      { name: currentName, slug: currentSlug },
      {
        onSuccess: (updated) => {
          if (updated.slug !== slug) {
            navigate(`${wsHome(updated.slug)}?settings=ws-general`, { replace: true });
          }
        },
      },
    );
  }

  function handleDelete(e: FormEvent) {
    e.preventDefault();
    if (deleteConfirm !== slug) return;
    deleteWs.mutate(undefined, {
      onSuccess: () => {
        setSearchParams((prev) => { prev.delete("settings"); return prev; });
        navigate("/");
      },
    });
  }

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <h2 className="text-lg font-semibold text-slate-100">{t("settings.tabs.wsGeneral")}</h2>
        {update.isSuccess && (
          <Banner kind="success">{t("settings.ws.saved")}</Banner>
        )}
        {update.error && !wsSlugErrorKind && (
          <Banner kind="error">{apiErrorMessage(update.error, tErr)}</Banner>
        )}
        {slugValue !== null && slugValue !== (ws.data?.slug ?? "") && (
          <Banner kind="info">{t("settings.ws.slugWarning")}</Banner>
        )}
        <form onSubmit={handleSave} className="space-y-4">
          <Field label={t("settings.ws.name")}>
            {({ id, ...ariaProps }) => (
              <TextInput
                id={id}
                value={currentName}
                onChange={(e) => setName(e.target.value)}
                disabled={!isOwner}
                {...ariaProps}
              />
            )}
          </Field>
          <Field
            label={t("settings.ws.slug")}
            error={
              wsSlugErrorKind === "reserved"
                ? t("settings.slugReserved")
                : wsSlugErrorKind === "taken"
                  ? t("settings.slugTaken")
                  : undefined
            }
          >
            {({ id, ...ariaProps }) => (
              <TextInput
                id={id}
                value={currentSlug}
                onChange={(e) => setSlugValue(e.target.value)}
                disabled={!isOwner}
                hasError={Boolean(wsSlugErrorKind)}
                {...ariaProps}
              />
            )}
          </Field>
          {isOwner && (
            <div className="flex justify-end">
              <Button variant="primary" type="submit" disabled={update.isPending}>
                {update.isPending ? <Spinner size="sm" /> : t("settings.save")}
              </Button>
            </div>
          )}
        </form>
      </div>

      {isOwner && (
        <div className="space-y-4 border-t border-slate-800 pt-8">
          <h3 className="text-sm font-semibold text-red-400">{t("settings.ws.dangerZone")}</h3>
          <p className="text-sm text-slate-400">{t("settings.ws.deleteWarning")}</p>
          {deleteWs.error && (
            <Banner kind="error">{apiErrorMessage(deleteWs.error, tErr)}</Banner>
          )}
          <form onSubmit={handleDelete} className="space-y-3">
            <Field label={t("settings.ws.deleteConfirmLabel", { slug })}>
              {({ id, ...ariaProps }) => (
                <TextInput
                  id={id}
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder={slug}
                  {...ariaProps}
                />
              )}
            </Field>
            <div className="flex justify-end">
              <Button
                variant="danger"
                type="submit"
                disabled={deleteConfirm !== slug || deleteWs.isPending}
              >
                {deleteWs.isPending ? <Spinner size="sm" /> : t("settings.ws.delete")}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function WsMembersPanel({ slug }: { slug: string }) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const ws = useWorkspace(slug);
  const members = useMembers(slug);
  const invites = useInvites(slug);
  const updateRole = useUpdateMemberRole(slug);
  const removeMember = useRemoveMember(slug);
  const revokeInvite = useRevokeInvite(slug);
  const [inviteOpen, setInviteOpen] = useState(false);
  const isOwner = ws.data?.role === "owner";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-100">{t("settings.tabs.wsMembers")}</h2>
        {isOwner && (
          <Button variant="secondary" size="sm" onClick={() => setInviteOpen(true)}>
            {t("settings.members.invite")}
          </Button>
        )}
      </div>

      {members.isLoading ? (
        <SkeletonRows rows={3} />
      ) : members.error ? (
        <Banner kind="error">{apiErrorMessage(members.error, tErr)}</Banner>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-700">
          <Table>
            <THead>
              <Tr>
                <Th>{t("settings.members.user")}</Th>
                <Th>{t("settings.members.role")}</Th>
                {isOwner && <Th className="w-20" />}
              </Tr>
            </THead>
            <TBody>
              {(members.data ?? []).map((m) => (
                <Tr key={m.userId}>
                  <Td>
                    <div className="flex items-center gap-3">
                      <Avatar name={m.displayName} size="sm" />
                      <div>
                        <p className="text-sm font-medium text-slate-100">{m.displayName}</p>
                        <p className="text-xs text-slate-400">{m.email}</p>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    {isOwner ? (
                      <select
                        value={m.role}
                        onChange={(e) =>
                          updateRole.mutate({
                            userId: m.userId,
                            role: e.target.value as "owner" | "member" | "viewer",
                          })
                        }
                        className="rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-arch-500"
                      >
                        <option value="viewer">{t("members.roles.viewer")}</option>
                        <option value="member">{t("members.roles.member")}</option>
                        <option value="owner">{t("members.roles.owner")}</option>
                      </select>
                    ) : (
                      <span className="text-sm capitalize text-slate-300">{t(`members.roles.${m.role}`)}</span>
                    )}
                  </Td>
                  {isOwner && (
                    <Td className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => removeMember.mutate(m.userId)}
                        disabled={removeMember.isPending}
                      >
                        {t("settings.members.remove")}
                      </Button>
                    </Td>
                  )}
                </Tr>
              ))}
            </TBody>
          </Table>
        </div>
      )}

      {(invites.data ?? []).filter((i) => !i.acceptedAt).length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-300">{t("settings.members.pendingInvites")}</h3>
          <div className="overflow-x-auto rounded-lg border border-slate-700">
            <Table>
              <THead>
                <Tr>
                  <Th>{t("settings.members.inviteRole")}</Th>
                  <Th>{t("settings.members.inviteExpires")}</Th>
                  {isOwner && <Th className="w-20" />}
                </Tr>
              </THead>
              <TBody>
                {(invites.data ?? [])
                  .filter((i) => !i.acceptedAt)
                  .map((inv) => (
                    <Tr key={inv.id}>
                      <Td className="capitalize text-slate-300">{t(`members.roles.${inv.role}`)}</Td>
                      <Td className="text-slate-400">
                        {inv.expiresAt
                          ? new Date(inv.expiresAt).toLocaleDateString()
                          : t("invite.noExpiry")}
                      </Td>
                      {isOwner && (
                        <Td className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300"
                            onClick={() => revokeInvite.mutate(inv.id)}
                            disabled={revokeInvite.isPending}
                          >
                            {t("settings.members.revoke")}
                          </Button>
                        </Td>
                      )}
                    </Tr>
                  ))}
              </TBody>
            </Table>
          </div>
        </div>
      )}

      <InviteModal slug={slug} open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
}

function WsTokensPanel({ slug }: { slug: string }) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const ws = useWorkspace(slug);
  // Repos the new-token modal can narrow a token to (and build an MCP URL from).
  const repos = useRepos(slug);
  const [filterWorkspace, setFilterWorkspace] = useState(true);
  const [newTokenOpen, setNewTokenOpen] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<TokenRow | null>(null);

  const workspaceId = filterWorkspace ? (ws.data?.id ?? null) : undefined;
  const tokens = useTokensList(workspaceId);
  const createToken = useCreateToken(workspaceId);
  const revokeToken = useRevokeToken(workspaceId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-100">{t("settings.tabs.wsTokens")}</h2>
        <Button variant="secondary" size="sm" onClick={() => setNewTokenOpen(true)}>
          {t("settings.tokens.new")}
        </Button>
      </div>

      <div className="flex gap-2">
        <Button
          variant={filterWorkspace ? "primary" : "ghost"}
          size="sm"
          onClick={() => setFilterWorkspace(true)}
        >
          {t("settings.tokens.thisWorkspace")}
        </Button>
        <Button
          variant={!filterWorkspace ? "primary" : "ghost"}
          size="sm"
          onClick={() => setFilterWorkspace(false)}
        >
          {t("settings.tokens.all")}
        </Button>
      </div>

      {tokens.isLoading ? (
        <SkeletonRows rows={3} />
      ) : tokens.error ? (
        <Banner kind="error">{apiErrorMessage(tokens.error, tErr)}</Banner>
      ) : (
        <TokensTable
          tokens={tokens.data ?? []}
          currentWorkspaceId={ws.data?.id ?? null}
          onRevoke={(token) => setPendingRevoke(token)}
        />
      )}

      <NewTokenModal
        open={newTokenOpen}
        currentWorkspaceId={ws.data?.id ?? null}
        currentWorkspaceName={ws.data?.name ?? ""}
        currentWorkspaceSlug={slug}
        repos={repos.data?.repos ?? []}
        onClose={() => setNewTokenOpen(false)}
        onSubmit={async (input) => {
          const result = await createToken.mutateAsync(input);
          return result;
        }}
      />

      <RevokeConfirmDialog
        token={pendingRevoke}
        pending={revokeToken.isPending}
        error={revokeToken.error ? apiErrorMessage(revokeToken.error, tErr) : null}
        onConfirm={(token) =>
          revokeToken.mutate(
            { id: token.id },
            { onSuccess: () => setPendingRevoke(null) },
          )
        }
        onCancel={() => setPendingRevoke(null)}
      />
    </div>
  );
}

function RepoGitPanel({ slug, repo }: { slug: string; repo: string }) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const ws = useWorkspace(slug);
  const isOwner = ws.data?.role === "owner";

  const [trunkBranch, setTrunkBranch] = useState("main");
  const [featureBranchPattern, setFeatureBranchPattern] = useState("arch/{slug}");
  const [protectTrunk, setProtectTrunk] = useState(true);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load current settings from API
  const { data: gitSettings } = useQuery({
    queryKey: ["repo-git", slug, repo],
    queryFn: () => apiFetch<{ trunkBranch: string; featureBranchPattern: string; protectTrunk: boolean }>(`/api/ws/${slug}/repos/${repo}/git-settings`),
    enabled: !!slug && !!repo,
  });

  // Sync form when data arrives (only on initial load)
  const [initialized, setInitialized] = useState(false);
  if (gitSettings && !initialized) {
    setTrunkBranch(gitSettings.trunkBranch);
    setFeatureBranchPattern(gitSettings.featureBranchPattern);
    setProtectTrunk(gitSettings.protectTrunk);
    setInitialized(true);
  }

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/api/ws/${slug}/repos/${repo}/git-settings`, {
        method: "PATCH",
        body: { trunkBranch, featureBranchPattern, protectTrunk },
      }),
    onSuccess: () => { setSaved(true); setError(null); },
    onError: (err: unknown) => {
      setSaved(false);
      setError(apiErrorMessage(err as never, tErr));
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    save.mutate();
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-slate-100">{t("settings.tabs.repoGit")}</h2>
      {saved && <Banner kind="success">{t("settings.git.saved")}</Banner>}
      {error && <Banner kind="error">{error}</Banner>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label={t("settings.git.trunkBranch")}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              value={trunkBranch}
              onChange={(e) => setTrunkBranch(e.target.value)}
              disabled={!isOwner}
              {...ariaProps}
            />
          )}
        </Field>
        <Field label={t("settings.git.featurePattern")}>
          {({ id, ...ariaProps }) => (
            <div className="space-y-1">
              <TextInput
                id={id}
                value={featureBranchPattern}
                onChange={(e) => setFeatureBranchPattern(e.target.value)}
                disabled={!isOwner}
                {...ariaProps}
              />
              <p className="text-xs text-slate-400">
                {t("settings.git.preview")}: <code className="font-mono text-slate-300">{previewBranch(featureBranchPattern)}</code>
              </p>
            </div>
          )}
        </Field>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="accent-arch-500 h-4 w-4"
            checked={protectTrunk}
            onChange={(e) => setProtectTrunk(e.target.checked)}
            disabled={!isOwner}
          />
          <span className="text-sm text-slate-200">{t("settings.git.protectTrunk")}</span>
        </label>
        {isOwner && (
          <div className="flex justify-end">
            <Button variant="primary" type="submit" disabled={save.isPending}>
              {save.isPending ? <Spinner size="sm" /> : t("settings.save")}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}

function RepoDangerPanel({ slug, repo }: { slug: string; repo: string }) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const ws = useWorkspace(slug);
  const isOwner = ws.data?.role === "owner";
  const [confirm, setConfirm] = useState("");

  const deleteRepo = useMutation({
    mutationFn: () =>
      apiFetch<void>(`/api/ws/${slug}/repos/${repo}`, { method: "DELETE" }),
    onSuccess: () => {
      setSearchParams((prev) => { prev.delete("settings"); return prev; });
      navigate(wsHome(slug));
    },
  });

  if (!isOwner) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-100">{t("settings.tabs.repoDanger")}</h2>
        <Banner kind="info">{t("settings.danger.ownerOnly")}</Banner>
      </div>
    );
  }

  function handleDelete(e: FormEvent) {
    e.preventDefault();
    if (confirm !== repo) return;
    deleteRepo.mutate();
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-red-400">{t("settings.tabs.repoDanger")}</h2>
      <p className="text-sm text-slate-400">{t("settings.danger.repoDeleteWarning")}</p>
      {deleteRepo.error && (
        <Banner kind="error">{apiErrorMessage(deleteRepo.error, tErr)}</Banner>
      )}
      <form onSubmit={handleDelete} className="space-y-3">
        <Field label={t("settings.danger.repoConfirmLabel", { repo })}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={repo}
              {...ariaProps}
            />
          )}
        </Field>
        <div className="flex justify-end">
          <Button
            variant="danger"
            type="submit"
            disabled={confirm !== repo || deleteRepo.isPending}
          >
            {deleteRepo.isPending ? <Spinner size="sm" /> : t("settings.danger.deleteRepo")}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ── imports needed for inline mutations ──────────────────────────────────────
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api/client";

// ── left rail tab item ────────────────────────────────────────────────────────

interface TabItemProps {
  tab: TabDef;
  active: boolean;
  onClick: () => void;
  t: (key: string) => string;
}

function TabItem({ tab, active, onClick, t }: TabItemProps) {
  return (
    <button
      onClick={onClick}
      className={[
        "flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors w-full",
        active
          ? "bg-arch-500/15 text-arch-200"
          : "text-slate-400 hover:bg-slate-800 hover:text-slate-100",
      ].join(" ")}
    >
      {tab.icon}
      {tab.rawLabel ?? t(tab.labelKey)}
    </button>
  );
}

// ── main modal ────────────────────────────────────────────────────────────────

export function SettingsModal() {
  const { t } = useTranslation("workspace");
  const { slug, repo } = useParams<{ slug?: string; repo?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get("settings");
  const isOpen = activeTab !== null;

  function close() {
    setSearchParams((prev) => { prev.delete("settings"); return prev; });
  }

  function selectTab(key: string) {
    setSearchParams((prev) => { prev.set("settings", key); return prev; });
  }

  const { refs, context } = useFloating({ open: isOpen });
  const dismiss = useDismiss(context, { escapeKey: true, outsidePress: false });
  const { getFloatingProps } = useInteractions([dismiss]);

  // Plugin-contributed settings pages (out-of-host — the modal mounts above the
  // plugin host), resolved + when-gated against the live ctx. Keyed `plugin:<id>`.
  const contributedPages = useOutOfHostSettingsPages();
  const hostCtx = useOutOfHostCtx();

  if (!isOpen) return null;

  const showWorkspace = !!slug;
  const showRepo = !!slug && !!repo;

  const scopeShown: Record<SettingsScope, boolean> = {
    account: true,
    workspace: showWorkspace,
    repo: showRepo,
  };
  const CONTRIB_PREFIX = "plugin:";
  const contribTabsFor = (scope: SettingsScope): TabDef[] =>
    scopeShown[scope]
      ? contributedPages
          .filter((p) => p.contribution.scope === scope)
          .map((p) => ({
            key: `${CONTRIB_PREFIX}${p.contribution.id}`,
            labelKey: "",
            rawLabel: p.contribution.title,
            icon: p.contribution.icon ?? null,
          }))
      : [];
  const accountTabs = [...ACCOUNT_TABS, ...contribTabsFor("account")];
  const workspaceTabs = showWorkspace ? [...WORKSPACE_TABS, ...contribTabsFor("workspace")] : [];
  const repoTabs = showRepo ? [...REPO_TABS, ...contribTabsFor("repo")] : [];

  // Resolve active tab — fall back to "profile" if key is unknown
  const allTabs = [...accountTabs, ...workspaceTabs, ...repoTabs];
  const allTabKeys = allTabs.map((x) => x.key);
  const resolvedTab = activeTab && allTabKeys.includes(activeTab) ? activeTab : "profile";

  function renderContributedPage(key: string) {
    const id = key.slice(CONTRIB_PREFIX.length);
    const page = contributedPages.find((p) => p.contribution.id === id);
    if (!page || !hostCtx) return null;
    const { Page } = page.contribution;
    return (
      <PluginBoundary pluginId={page.pluginId}>
        <Page ctx={hostCtx} />
      </PluginBoundary>
    );
  }

  function renderPanel() {
    if (resolvedTab.startsWith(CONTRIB_PREFIX)) return renderContributedPage(resolvedTab);
    switch (resolvedTab) {
      case "profile": return <ProfilePanel />;
      case "password": return <PasswordPanel />;
      case "ws-general": return slug ? <WsGeneralPanel slug={slug} /> : null;
      case "ws-members": return slug ? <WsMembersPanel slug={slug} /> : null;
      case "ws-tokens": return slug ? <WsTokensPanel slug={slug} /> : null;
      case "repo-git": return slug && repo ? <RepoGitPanel slug={slug} repo={repo} /> : null;
      case "repo-mcp": return slug && repo ? <McpSetupPanel slug={slug} repo={repo} /> : null;
      case "repo-danger": return slug && repo ? <RepoDangerPanel slug={slug} repo={repo} /> : null;
      default: return null;
    }
  }

  // Resolve active tab label for the right-pane header
  const activeTabDef = allTabs.find((x) => x.key === resolvedTab);
  const activeTabTitle = activeTabDef
    ? (activeTabDef.rawLabel ?? t(activeTabDef.labelKey))
    : t("settings.heading");

  return (
    <FloatingPortal>
      {/* Dark backdrop — covers everything behind the dialog */}
      <FloatingOverlay
        lockScroll
        className="fixed inset-0 z-overlay bg-black/60 backdrop-blur-sm"
      />
      <FloatingFocusManager context={context}>
        {/* Dialog panel: large rounded card, inset 16px from edges */}
        <div
          ref={refs.setFloating}
          role="dialog"
          aria-modal="true"
          aria-label={t("settings.heading")}
          className="fixed inset-4 z-overlay flex overflow-hidden rounded-xl border border-slate-700 bg-slate-900 shadow-2xl shadow-black/60 focus:outline-none"
          {...getFloatingProps()}
        >
          {/* Left rail */}
          <nav className="flex w-44 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-slate-800 bg-slate-900/60 p-3">
            <p className="px-3 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {t("settings.groups.account")}
            </p>
            {accountTabs.map((tab) => (
              <TabItem
                key={tab.key}
                tab={tab}
                active={resolvedTab === tab.key}
                onClick={() => selectTab(tab.key)}
                t={t}
              />
            ))}

            {showWorkspace && (
              <>
                <p className="px-3 pb-1.5 pt-5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  {t("settings.groups.workspace")}
                </p>
                {workspaceTabs.map((tab) => (
                  <TabItem
                    key={tab.key}
                    tab={tab}
                    active={resolvedTab === tab.key}
                    onClick={() => selectTab(tab.key)}
                    t={t}
                  />
                ))}
              </>
            )}

            {showRepo && (
              <>
                <p className="px-3 pb-1.5 pt-5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  {t("settings.groups.repository")}
                </p>
                {repoTabs.map((tab) => (
                  <TabItem
                    key={tab.key}
                    tab={tab}
                    active={resolvedTab === tab.key}
                    onClick={() => selectTab(tab.key)}
                    t={t}
                  />
                ))}
              </>
            )}
          </nav>

          {/* Right pane: sticky header with active tab name + close, then scrollable body */}
          <div className="flex min-w-0 flex-1 flex-col bg-slate-950">
            <div className="flex shrink-0 items-center justify-between border-b border-slate-800 px-6 py-3">
              <h2 className="text-sm font-semibold text-slate-100">
                {activeTabTitle}
              </h2>
              <button
                onClick={close}
                aria-label={t("settings.close")}
                className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-arch-400"
              >
                <IconClose />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="mx-auto w-full max-w-2xl px-8 py-6">
                {renderPanel()}
              </div>
            </div>
          </div>
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}
